/**
 * Hero - Landing page hero section (v11.1)
 *
 * Video-first: a single centered column, not the old left/right grid. The H1
 * stays keyword-led ("Learn vocabulary / from YouTube.") and first in the DOM
 * (SEO), but the video is the dominant element. The landscape hero-loop clip
 * (bilingual panel → one line into Piccard → candidate cards; clip UI is
 * zh-CN, so the caption bar under it retells the flow in the visitor's
 * locale) sits big and centered, with the two 9:16 phone clips beneath it in
 * a row — the "life on your phone" proof (flashcard that replays the YouTube
 * moment, and review-on-the-go). Primary CTA is the extension (CWS install,
 * v11 extension-first entry); signup is secondary.
 *
 * Entrance fades only; reduced-motion respected.
 */

import { useEffect, useRef, useState } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import { ArrowRight, ChevronDown, ExternalLink } from 'lucide-react'
import { CWS_URL } from './ExtensionPromo'
import { LanguageRow } from './LanguageRow'
import { useLandingT, usePublicHref } from './landing-locale'

const EASE_OUT_EXPO: [number, number, number, number] = [0.16, 1, 0.3, 1]

/** Lazy muted loop: mounts the <video> only near-viewport, poster otherwise.
 *  mp4 source first: iOS WeChat/WKWebView has no WebM. Reduced-motion → poster.
 *  v10-C2: webm-first when the browser can actually play VP9 — the 3.2MB mp4
 *  was winning the source-order race on Chrome/Android over the 1.4MB webm. */
function LoopVideo({ mp4, webm, poster, alt, className = 'aspect-video overflow-hidden rounded-xl border border-black/10 bg-black shadow-card' }: {
  mp4: string
  webm: string
  poster: string
  alt: string
  className?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [show, setShow] = useState(false)

  const canWebm =
    typeof document !== 'undefined' &&
    !!document.createElement('video').canPlayType('video/webm; codecs=vp9')

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShow(true)
          io.disconnect()
        }
      },
      { rootMargin: '200px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  const sources = canWebm ? [webm, mp4] : [mp4, webm]

  return (
    <div ref={ref} className={className}>
      {show ? (
        <video poster={poster} autoPlay muted loop playsInline className="h-full w-full object-cover" aria-label={alt}>
          {sources.map((src) => (
            <source key={src} src={src} type={src.endsWith('.webm') ? 'video/webm' : 'video/mp4'} />
          ))}
        </video>
      ) : (
        <img src={poster} alt={alt} className="h-full w-full object-cover object-top" />
      )}
    </div>
  )
}

/** The two 9:16 phone clips shown under the landscape hero-loop. */
const PHONE_CLIPS = [
  {
    mp4: '/landing/hero-phone-1.mp4',
    webm: '/landing/hero-phone-1.webm',
    poster: '/landing/hero-phone-1.jpg',
    alt: 'A flashcard on your phone that replays the YouTube moment it came from',
  },
  {
    mp4: '/landing/hero-phone-2.mp4',
    webm: '/landing/hero-phone-2.webm',
    poster: '/landing/hero-phone-2.jpg',
    alt: 'Reviewing flashcards on the go — walking with your phone in hand',
  },
] as const

const PHONE_FRAME = 'aspect-[9/16] overflow-hidden rounded-[1.5rem] border border-black/10 bg-black shadow-card'

export function Hero() {
  const prefersReducedMotion = useReducedMotion()
  const t = useLandingT()
  const href = usePublicHref()

  const fadeUp = (delay: number, y = 20) =>
    prefersReducedMotion
      ? { initial: undefined, animate: undefined, transition: { duration: 0 } }
      : { initial: { opacity: 0, y }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, delay, ease: EASE_OUT_EXPO } }

  return (
    <section className="min-h-screen supports-[height:100dvh]:min-h-[100dvh] bg-canvas pt-28 md:pt-36 pb-12 flex flex-col items-center justify-center relative overflow-hidden px-4">
      <div className="w-full max-w-6xl mx-auto flex flex-col items-center">
        {/* Headline — keyword-led, first in DOM for SEO. */}
        <motion.h1
          className="font-headline font-extrabold text-[32px] md:text-[56px] lg:text-[64px] leading-[0.9] tracking-tight text-ink text-center"
          initial={prefersReducedMotion ? undefined : { opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.8, ease: EASE_OUT_EXPO }}
        >
          {t('landing.hero.title').split('\n').map((line, i) => (
            <span key={i} className="block">{line}</span>
          ))}
        </motion.h1>

        {/* Sub-headline — one line, kept small so the video dominates. */}
        <motion.p
          className="font-body text-[15px] md:text-[18px] text-ink/70 leading-relaxed text-center max-w-2xl mt-4"
          role="doc-subtitle"
          {...fadeUp(0.15)}
        >
          {t('landing.hero.subtitle')}
        </motion.p>

        {/* Landscape video — the promise in motion, big and centered. */}
        <motion.div className="mt-8 w-full max-w-3xl" {...fadeUp(0.25)}>
          <LoopVideo
            mp4="/landing/hero-loop.mp4"
            webm="/landing/hero-loop.webm"
            poster="/landing/hero-loop.jpg"
            alt="Bilingual subtitle panel on YouTube, one line brought into Piccard, candidate cards returned"
          />
          {/* Clip UI is zh-CN (accepted, no re-shoot) — this caption retells
              the flow in the visitor's locale so the clip never reads as a
              mistake. It describes exactly what the clip shows, no more. */}
          <p className="mt-2 text-center text-xs text-on-surface-variant">
            {t('landing.hero.videoCaption')}
          </p>
        </motion.div>

        {/* Phone clips — two 9:16 proofs of the loop on your phone, side by side. */}
        <motion.div className="mt-6 grid w-full max-w-md grid-cols-2 gap-4" {...fadeUp(0.35)}>
          {PHONE_CLIPS.map((c) => (
            <LoopVideo key={c.mp4} mp4={c.mp4} webm={c.webm} poster={c.poster} alt={c.alt} className={PHONE_FRAME} />
          ))}
        </motion.div>

        {/* CTA — extension-first: CWS install is the PRIMARY (filled) button,
            signup demoted to secondary (outline). */}
        <motion.div className="mt-8 flex flex-col items-center gap-3" {...fadeUp(0.45, 10)}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={CWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-7 py-3.5 rounded-full bg-primary text-on-primary font-semibold text-base shadow-card hover:bg-primary-active transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
            >
              {t('landing.hero.ctaExtension')}
              <ExternalLink className="ml-2 w-4 h-4" />
            </a>
            <a
              href={href('/login')}
              className="inline-flex items-center px-6 py-3.5 rounded-full border-2 border-outline-variant text-on-surface font-semibold text-base hover:bg-surface-container-low transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
            >
              {t('landing.hero.cta')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </div>
          <p className="text-muted-soft text-xs">{t('landing.hero.ctaNote')}</p>
        </motion.div>

        {/* Language support — muted flag strip, the fold's trust signal. */}
        <div className="mt-7">
          <LanguageRow />
        </div>
      </div>

      {/* Scroll indicator — to the How-it-works section. */}
      <motion.div
        className="mt-10 md:mt-14"
        initial={prefersReducedMotion ? undefined : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.6, delay: 1.2 }}
      >
        <a
          href="#how"
          onClick={(e) => {
            e.preventDefault()
            document.getElementById('how')?.scrollIntoView({ behavior: 'smooth' })
          }}
          className="inline-flex flex-col items-center text-muted hover:text-ink transition-colors"
          aria-label="Scroll to learn more"
        >
          <ChevronDown className="w-6 h-6" />
        </a>
      </motion.div>
    </section>
  )
}
